import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Card, Badge, Text, Stack, Flex, ActionIcon, Drawer, Select, Button, Group, Divider, Indicator } from '@mantine/core';
import { CCMSDataTable, Tooltip, TextInput, StreamProgress, ListPagination } from '@/components';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import {
  useHttpClient,
  useBatchStreaming,
  useACCourseService,
  AccoursesStreamingService,
  FacilitatorRegistrationView,
  CourseDateModelView,
} from '@actschurch/ccms-sdk';
import { IconEye, IconRefresh, IconSearch, IconFilter } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles, noWrap } from '@/utils/tableStyles';
import { sortRows } from '@/utils/tableSort';
import type { SortConfig } from '@/components/ccms-components/types/common';

const PAGE_SIZE = 10;

interface RegistrationTabProps {
  userId: string;
  active: boolean;
}

/**
 * "NameA", "NameA & NameB", or "NameA, NameB & NameC" — falls back to the bare count (or an
 * em dash) if the API ever sends a count without names, so the column never goes blank.
 */
const formatParticipants = (row: FacilitatorRegistrationView): string => {
  const names = (row.participantNames ?? []).filter(Boolean);
  if (names.length === 0) return row.participantCount ? String(row.participantCount) : '—';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
};

/**
 * Registrations this facilitator is assigned to.
 *
 * Reads `GET /api/facilitators/{userId}/registrations` through the SDK's
 * `AccoursesStreamingService.streamRegistrationsByFacilitator`, which is cache-composed
 * server-side and yields the (usually small) set in batches.
 */
export default function RegistrationTab({ userId, active }: RegistrationTabProps) {
  const http = useHttpClient();
  const navigate = useNavigate();
  const courseService = useACCourseService();

  const [searchTerm, setSearchTerm] = useState('');
  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  // ── Filter drawer state ───────────────────────────────────────────────────
  const [filterOpen, setFilterOpen] = useState(false);
  const [courseFilter, setCourseFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<string | null>(null);

  // Applied filters, separate from the drawer's draft state, so opening the drawer to poke
  // around doesn't affect the table until "Apply" is pressed.
  const [appliedCourse, setAppliedCourse] = useState<string | null>(null);
  const [appliedStatus, setAppliedStatus] = useState<string | null>(null);
  const [appliedDate, setAppliedDate] = useState<string | null>(null);

  // Intake dates for the currently-selected course in the drawer. Fetched from the course
  // service (there's no "all dates across courses" endpoint), so this only loads once a
  // course is chosen — the date dropdown is disabled/empty until then.
  const [courseDates, setCourseDates] = useState<CourseDateModelView[]>([]);
  const [courseDatesLoading, setCourseDatesLoading] = useState(false);

  const makeStream = useCallback(
    () => new AccoursesStreamingService(http).streamRegistrationsByFacilitator(userId, 50),
    [http, userId],
  );

  const {
    items: registrations,
    isLoading: loading,
    progress,
    loadedCount,
    load,
  } = useBatchStreaming<FacilitatorRegistrationView>(makeStream, [http, userId]);

  useEffect(() => {
    if (active && userId) load();
  }, [active, userId, load]);

  // ── Filter option sources — derived from the registrations already streamed in, per the
  //    "get statuses/courses from the registration call" note, rather than separate lookups ──
  const courseOptions = useMemo(() => {
    const seen = new Map<string, string>();
    registrations.forEach((r) => {
      if (r.courseID != null) seen.set(String(r.courseID), r.courseName ?? `Course ${r.courseID}`);
    });
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [registrations]);

  const statusOptions = useMemo(() => {
    const seen = new Map<string, string>();
    registrations.forEach((r) => {
      if (r.statusID != null) seen.set(String(r.statusID), r.statusName ?? `Status ${r.statusID}`);
    });
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [registrations]);

  // Fetch intake dates for the course selected *in the drawer* (courseFilter), so the date
  // dropdown reflects the current picker state as the person filters, not the last-applied one.
  useEffect(() => {
    if (!courseFilter) {
      setCourseDates([]);
      setDateFilter(null);
      return;
    }
    let cancelled = false;
    setCourseDatesLoading(true);
    courseService.getDates(Number(courseFilter))
      .then((result) => { if (!cancelled) setCourseDates(result ?? []); })
      .catch((e) => { console.error('Error loading intake dates:', e); if (!cancelled) setCourseDates([]); })
      .finally(() => { if (!cancelled) setCourseDatesLoading(false); });
    return () => { cancelled = true; };
  }, [courseFilter, courseService]);

  const dateOptions = useMemo(
    () => courseDates.map((d) => ({
      value: String(d.dateID),
      label: d.periodDisplay ?? d.startDisplay ?? `Date ${d.dateID}`,
    })),
    [courseDates],
  );

  const activeFilterCount = [appliedCourse, appliedStatus, appliedDate].filter(Boolean).length;

  const openFilterDrawer = () => {
    // Seed the drawer's draft from whatever's currently applied, so re-opening shows the
    // active filters rather than resetting to blank.
    setCourseFilter(appliedCourse);
    setStatusFilter(appliedStatus);
    setDateFilter(appliedDate);
    setFilterOpen(true);
  };

  const applyFilters = () => {
    setAppliedCourse(courseFilter);
    setAppliedStatus(statusFilter);
    setAppliedDate(dateFilter);
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setCourseFilter(null);
    setStatusFilter(null);
    setDateFilter(null);
    setAppliedCourse(null);
    setAppliedStatus(null);
    setAppliedDate(null);
  };

  // ── Search — matches ID, reference, course, location, status, participants ──
  const newestFirstRegistrations = useMemo(
    () => [...registrations].sort((a, b) => (b.registrationID ?? 0) - (a.registrationID ?? 0)),
    [registrations],
  );

  const selectedDatePeriod = useMemo(
    () => (appliedDate ? courseDates.find((d) => String(d.dateID) === appliedDate)?.periodDisplay ?? null : null),
    [appliedDate, courseDates],
  );

  const filteredRegistrations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return newestFirstRegistrations.filter((row) => {
      if (appliedCourse && String(row.courseID) !== appliedCourse) return false;
      if (appliedStatus && String(row.statusID) !== appliedStatus) return false;
      // ⚠️ FacilitatorRegistrationView carries no dateID — matching on periodDisplay text is
      // the only link available between a registration row and a specific intake date.
      if (selectedDatePeriod && row.periodDisplay !== selectedDatePeriod) return false;

      if (!term) return true;
      const matchesText = [row.reference, row.courseName, row.locationName, row.statusName, row.periodDisplay]
        .some((field) => (field ?? '').toLowerCase().includes(term));
      const matchesParticipant = (row.participantNames ?? []).some((n) =>
        (n ?? '').toLowerCase().includes(term),
      );
      return matchesText || matchesParticipant || String(row.registrationID ?? '').includes(term);
    });
  }, [newestFirstRegistrations, searchTerm, appliedCourse, appliedStatus, selectedDatePeriod]);

  // Search or filter changes should always land back on page 1, or a narrowed result set can
  // point at a page that no longer exists and render an empty table with matches sitting on page 1.
  useEffect(() => { setActivePage(1); }, [searchTerm, appliedCourse, appliedStatus, appliedDate]);

  const columns: TableColumn<FacilitatorRegistrationView>[] = [
    {
      // Reference is the human-facing identifier; the numeric ID rides along underneath
      // rather than eating a column of its own.
      key: 'reference',
      header: 'Registration',
      accessor: (row) => row.reference ?? '—',
      sortable: true,
      width: 210,
      render: (_, row) => (
        <Stack gap={0}>
          <Text size="sm" fw={600} style={noWrap}>
            {row.reference || `Registration ${row.registrationID}`}
          </Text>
          <Text size="xs" c="dimmed">#{row.registrationID}</Text>
        </Stack>
      ),
    },
    {
      key: 'courseName',
      header: 'Course',
      accessor: (row) => row.courseName ?? '—',
      sortable: true,
      width: 200,
      render: (value, row) =>
        row.courseName ? (
          <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(row.courseColor)}>
            {value}
          </Badge>
        ) : (
          <Text size="sm" c="dimmed">—</Text>
        ),
    },
    {
      // Period + where it runs — two related facts that read better stacked than as
      // two competing columns (the old Location column was usually just a dash).
      key: 'periodDisplay',
      header: 'Period',
      accessor: (row) => row.periodDisplay ?? '—',
      sortable: true,
      width: 210,
      render: (_, row) => {
        const place =
          [row.locationName, row.isRemote ? 'Remote' : null].filter(Boolean).join(' · ') || '—';
        return (
          <Stack gap={0}>
            <Text size="sm" style={noWrap}>{row.periodDisplay || '—'}</Text>
            <Text size="xs" c="dimmed" style={noWrap}>{place}</Text>
          </Stack>
        );
      },
    },
    {
      key: 'participantCount',
      header: 'Participants',
      accessor: (row) => row.participantCount ?? 0,
      sortable: true,
      width: 200,
      render: (_, row) => (
        <Text size="sm" style={{ wordBreak: 'break-word' }}>{formatParticipants(row)}</Text>
      ),
    },
    {
      key: 'nextSessionDisplay',
      header: 'Next Session',
      accessor: (row) => row.nextSessionDisplay ?? '—',
      sortable: true,
      width: 190,
      render: (_, row) => (
        <Text size="sm" style={noWrap}>{row.nextSessionDisplay || '—'}</Text>
      ),
    },
    {
      key: 'statusName',
      header: 'Status',
      accessor: (row) => row.statusName ?? '—',
      sortable: true,
      align: 'center',
      width: 140,
      render: (value, row) => (
        <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(row.statusColor)}>
          {value}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      accessor: (row) => row,
      sortable: false,
      align: 'center',
      width: 70,
      render: (_, row) => (
        <Tooltip label="View">
          <ActionIcon
            variant="light"
            color="blue"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/Grow/Registration/RegistrationDetails/${row.registrationID}`);
            }}
          >
            <IconEye size={14} />
          </ActionIcon>
        </Tooltip>
      ),
    },
  ];

  const sortedRegistrations = useMemo(
    () => sortRows(filteredRegistrations, columns, sortConfig),
    [filteredRegistrations, columns, sortConfig],
  );

  const paginatedRegistrations = useMemo(
    () => sortedRegistrations.slice((activePage - 1) * pageSize, activePage * pageSize),
    [sortedRegistrations, activePage, pageSize],
  );

  return (
    <Card withBorder radius="md" padding="lg">
      <Flex justify="space-between" align="center" mb="md">
        <TextInput
          placeholder="Search by ID, reference, course, location, or participant"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          radius="md"
          styles={{ input: { width: '380px' } }}
          size="sm"
        />
        <Group gap={6}>
          <Indicator label={activeFilterCount} size={16} disabled={activeFilterCount === 0} color="red" offset={4}>
              <Button
                variant="outline"
                color="blue"
                leftSection={<IconFilter size={16} />}
                onClick={openFilterDrawer}
              >
                Filters
              </Button>
          </Indicator>
          <Tooltip label="Refresh">
            <ActionIcon variant="subtle" onClick={() => load()} loading={loading} size="lg">
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Flex>

      <StreamProgress
        loading={loading}
        progress={progress}
        loadedCount={loadedCount}
        label="registrations"
      />

      {filteredRegistrations.length === 0 && !loading ? (
        <Text size="sm" c="dimmed" ta="center">
          {registrations.length === 0
            ? 'No registrations found for this facilitator.'
            : 'No registrations match your search or filters.'}
        </Text>
      ) : (
        <>
          {/* The table is wide; let it scroll inside the card rather than compressing columns
             until badges and dates ellipsise. Below 48em `ui-responsive-table` turns rows into
             cards, so this only bites on mid-width desktop windows. */}
          <Box style={{ overflowX: 'auto' }}>
            <CCMSDataTable
              data={paginatedRegistrations}
              columns={columns}
              loading={loading && registrations.length === 0}
              sortable
              onSort={(config) => { setSortConfig(config); setActivePage(1); }}
              onRowClick={(row) => navigate(`/Grow/Registration/RegistrationDetails/${row.registrationID}`)}
              striped
              highlightOnHover
            />
          </Box>
          <ListPagination
            page={activePage}
            pageSize={pageSize}
            totalItems={filteredRegistrations.length}
            onPageChange={setActivePage}
            onPageSizeChange={(size) => { setPageSize(size); setActivePage(1); }}
            showPageSize
          />
        </>
      )}

      <Drawer
        opened={filterOpen}
        onClose={() => setFilterOpen(false)}
        title={<Text fw={600} size="md">Filter Registrations</Text>}
        position="right"
        size="sm"
        padding="md"
      >
        <Stack gap="md">
          <Select
            label="Course"
            placeholder="All courses"
            data={courseOptions}
            value={courseFilter}
            onChange={(val) => { setCourseFilter(val); setDateFilter(null); }}
            searchable
            clearable
          />

          <Select
            label="Status"
            placeholder="All statuses"
            data={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            searchable
            clearable
          />

          <Select
            label="Intake Date"
            placeholder={courseFilter ? 'All dates' : 'Select a course first'}
            data={dateOptions}
            value={dateFilter}
            onChange={setDateFilter}
            disabled={!courseFilter}
            searchable
            clearable
            rightSection={courseDatesLoading ? undefined : undefined}
          />

          <Divider />

          <Group justify="space-between">
            <Button variant="subtle" onClick={clearFilters}>Clear</Button>
            <Button onClick={applyFilters}>Apply</Button>
          </Group>
        </Stack>
      </Drawer>
    </Card>
  );
}